<template>
  <div class="dashboard-fleet-health">
    <!-- Header -->
    <div class="health-header">
      <div class="health-header-text">
        <h3 class="health-title">{{ $t('org-dashboard.health-title') }}</h3>
        <div class="health-availability" :style="{ color: availabilityColor }">
          {{ operationalAvailability }}%
        </div>
        <div class="health-subtitle">
          {{ $t('org-dashboard.health-availability-badge', { eligible, excluded }) }}
        </div>
        <div v-if="excludedBreakdown" class="health-excluded-detail">
          {{ $t('org-dashboard.health-excluded-detail', excludedBreakdown) }}
        </div>
        <div class="health-explanation">
          {{ $t('org-dashboard.health-explanation') }}
        </div>
      </div>
    </div>

    <!-- Main content: 2 columns -->
    <div class="health-main">
      <!-- Left: Donut + Legend -->
      <div class="health-left">
        <div class="health-donut-section">
          <div class="health-donut-wrap">
            <highcharts :options="donutOptions" />
            <div class="health-donut-center">
              <span class="health-donut-value">{{ eligible }}</span>
              <span class="health-donut-label">{{ $t('org-dashboard.health-eligible') }}</span>
            </div>
          </div>
          <div class="health-legend">
            <div
              v-for="key in severityKeys"
              :key="key"
              class="health-legend-item"
              :class="{ 'health-legend-item--dimmed': severities[key].nonEligible }"
              @click="$emit('severity-click', key)"
            >
              <span class="health-legend-dot" :style="{ backgroundColor: severities[key].color }"></span>
              <v-icon x-small :color="severities[key].color" class="mr-1">{{ severities[key].icon }}</v-icon>
              <span class="health-legend-label">{{ $t('org-dashboard.health-' + key) }}</span>
              <span class="health-legend-count">{{ bySeverity[key] || 0 }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Histogram -->
      <div class="health-right">
        <h4 class="health-section-title">{{ $t('org-dashboard.health-offline-histogram') }}</h4>
        <highcharts v-if="histogramOptions" :options="histogramOptions" />
        <div v-else class="health-histogram-empty">
          <v-icon color="#d1d5db" size="32">mdi-chart-bar</v-icon>
          <span>{{ $t('org-dashboard.health-no-data') }}</span>
        </div>
      </div>
    </div>

    <!-- Backlog or All Clear -->
    <div v-if="hasBacklog" class="health-backlog-grid">
      <div class="health-backlog-table">
        <div class="health-backlog-header">
          <h4 class="health-section-title">{{ $t('org-dashboard.health-backlog-incident') }}</h4>
          <v-btn v-if="backlogIncident.length" x-small text color="primary" @click="$emit('view-all', 'incident')">
            {{ $t('org-dashboard.health-view-all') }}
          </v-btn>
        </div>
        <table v-if="backlogIncident.length" class="health-table">
          <thead>
            <tr>
              <th>{{ $t('org-dashboard.health-identifier') }}</th>
              <th>{{ $t('org-dashboard.health-location') }}</th>
              <th>{{ $t('org-dashboard.health-duration') }}</th>
              <th>EVSE</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in backlogIncident" :key="item.id" class="health-table-row" @click="$emit('charger-click', item.id)">
              <td class="health-table-id">{{ item.identifier }}</td>
              <td>{{ item.locationName }}</td>
              <td>{{ item.offlineDurationHuman }}</td>
              <td>{{ item.offlineEvses }}/{{ item.totalEvses }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="health-no-data">{{ $t('org-dashboard.health-no-data') }}</div>
      </div>

      <div class="health-backlog-table">
        <div class="health-backlog-header">
          <h4 class="health-section-title">{{ $t('org-dashboard.health-backlog-critical') }}</h4>
          <v-btn v-if="backlogCritical.length" x-small text color="primary" @click="$emit('view-all', 'critical')">
            {{ $t('org-dashboard.health-view-all') }}
          </v-btn>
        </div>
        <table v-if="backlogCritical.length" class="health-table">
          <thead>
            <tr>
              <th>{{ $t('org-dashboard.health-identifier') }}</th>
              <th>{{ $t('org-dashboard.health-location') }}</th>
              <th>{{ $t('org-dashboard.health-duration') }}</th>
              <th>EVSE</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in backlogCritical" :key="item.id" class="health-table-row" @click="$emit('charger-click', item.id)">
              <td class="health-table-id">{{ item.identifier }}</td>
              <td>{{ item.locationName }}</td>
              <td>{{ item.offlineDurationHuman }}</td>
              <td>{{ item.offlineEvses }}/{{ item.totalEvses }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else class="health-no-data">{{ $t('org-dashboard.health-no-data') }}</div>
      </div>
    </div>

    <!-- All clear card -->
    <div v-else class="health-all-clear">
      <v-icon color="#22c55e" class="mr-2">mdi-check-circle</v-icon>
      <div>
        <div class="health-all-clear-title">{{ $t('org-dashboard.health-no-urgent-action') }}</div>
        <div class="health-all-clear-text">{{ $t('org-dashboard.health-all-clear') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

const SEVERITIES = {
  online:      { color: '#22c55e', icon: 'mdi-check-circle',  nonEligible: false },
  warning:     { color: '#eab308', icon: 'mdi-alert',         nonEligible: false },
  incident:    { color: '#f97316', icon: 'mdi-alert-circle',  nonEligible: false },
  critical:    { color: '#ef4444', icon: 'mdi-clock',         nonEligible: false },
  inactive:    { color: '#9ca3af', icon: 'mdi-power-off',     nonEligible: true },
  maintenance: { color: '#9ca3af', icon: 'mdi-wrench',        nonEligible: true },
}

const HISTOGRAM_COLORS = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444', '#dc2626', '#991b1b']

export default {
  name: 'DashboardFleetHealth',
  components: {
    highcharts: Chart,
  },
  props: {
    operationalAvailability: { type: Number, default: 0 },
    eligible: { type: Number, default: 0 },
    excluded: { type: Number, default: 0 },
    excludedBreakdown: { type: Object, default: null },
    bySeverity: { type: Object, default: () => ({}) },
    offlineHistogram: { type: Array, default: () => [] },
    backlogIncident: { type: Array, default: () => [] },
    backlogCritical: { type: Array, default: () => [] },
  },

  data() {
    return {
      severities: SEVERITIES,
      severityKeys: Object.keys(SEVERITIES),
    }
  },

  computed: {
    availabilityColor() {
      if (this.operationalAvailability >= 95) return '#22c55e'
      if (this.operationalAvailability >= 80) return '#eab308'
      return '#ef4444'
    },

    hasBacklog() {
      return this.backlogIncident.length > 0 || this.backlogCritical.length > 0
    },

    hasHistogramData() {
      return this.offlineHistogram?.length > 0 && this.offlineHistogram.some(b => b.count > 0)
    },

    donutOptions() {
      const data = this.severityKeys
        .map(key => ({
          name: key,
          y: this.bySeverity[key] || 0,
          color: SEVERITIES[key].nonEligible
            ? this.withOpacity(SEVERITIES[key].color, 0.5)
            : SEVERITIES[key].color,
        }))
        .filter(d => d.y > 0)

      const isEmpty = !data.length

      return {
        chart: {
          type: 'pie',
          height: 180,
          width: 180,
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        title: { text: '' },
        credits: { enabled: false },
        tooltip: {
          enabled: !isEmpty,
          pointFormat: '<b>{point.y}</b> ({point.percentage:.1f}%)',
        },
        plotOptions: {
          pie: {
            innerSize: '68%',
            dataLabels: { enabled: false },
            borderWidth: 0,
            cursor: isEmpty ? 'default' : 'pointer',
            point: {
              events: {
                click: isEmpty ? undefined : (e) => {
                  this.$emit('severity-click', e.point.name)
                },
              },
            },
            states: { hover: { brightness: isEmpty ? 0 : -0.05 } },
          },
        },
        series: [{ data: isEmpty ? [{ name: 'empty', y: 1, color: '#e5e7eb' }] : data }],
      }
    },

    histogramOptions() {
      if (!this.hasHistogramData) return null

      return {
        chart: {
          type: 'column',
          height: 200,
          backgroundColor: 'transparent',
        },
        title: { text: '' },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          categories: this.offlineHistogram.map(b => b.label),
          labels: { style: { fontSize: '10px', color: '#6b7280' } },
        },
        yAxis: {
          title: { text: '' },
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          gridLineColor: '#f3f4f6',
        },
        tooltip: {
          formatter() {
            return `<b>${this.x}</b><br/>${this.y} ${this.series.chart.userOptions._chargersUnit || ''}`
          },
        },
        _chargersUnit: this.$t('org-dashboard.health-chargers-unit'),
        plotOptions: {
          column: {
            borderRadius: 4,
            borderWidth: 0,
            colorByPoint: true,
            colors: HISTOGRAM_COLORS.slice(0, this.offlineHistogram.length),
          },
        },
        series: [{
          data: this.offlineHistogram.map(b => b.count),
        }],
      }
    },
  },

  methods: {
    withOpacity(hex, opacity) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-fleet-health {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.health-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
}

.health-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.health-availability {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}

.health-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.health-excluded-detail {
  font-size: 10px;
  color: #9ca3af;
  margin-top: 1px;
}

.health-explanation {
  font-size: 10px;
  color: #9ca3af;
  font-style: italic;
  margin-top: 4px;
}

.health-main {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 16px;

  @media (min-width: 960px) {
    grid-template-columns: auto 1fr;
  }
}

.health-left {
  min-width: 0;
}

.health-right {
  min-width: 0;
  border-left: 1px solid #f3f4f6;
  padding-left: 20px;
}

.health-donut-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.health-donut-wrap {
  flex-shrink: 0;
  position: relative;
}

.health-donut-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.health-donut-value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.health-donut-label {
  font-size: 9px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.health-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.health-legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4b5563;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: #f3f4f6;
  }

  &--dimmed {
    opacity: 0.6;
  }

}

.health-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.health-legend-label {
  flex: 1;
}

.health-legend-count {
  font-weight: 700;
  color: #1f2937;
}

.health-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
}

.health-backlog-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  border-top: 1px solid #f3f4f6;
  padding-top: 16px;

  @media (min-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }
}

.health-backlog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.health-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;

  th {
    text-align: left;
    font-weight: 600;
    color: #6b7280;
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  td {
    padding: 6px 8px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
  }
}

.health-table-row {
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }
}

.health-table-id {
  font-weight: 600;
  color: #1976d2;
}

.health-no-data {
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  padding: 16px;
}

.health-histogram-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 16px;
  color: #9ca3af;
  font-size: 12px;
}

.health-all-clear {
  display: flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.health-all-clear-title {
  font-size: 13px;
  font-weight: 700;
  color: #166534;
}

.health-all-clear-text {
  font-size: 11px;
  color: #15803d;
  margin-top: 2px;
}
</style>
